<template>
  <div
    v-show="isActive"
    class="swiper-item"
  >
    <div
      class="scale-item"
      :class="{ 'show-collection': showNewUI }"
    >
      <div class="video-poster">
        <video-player
          v-if="isCreatePlayer"
          ref="playerRef"
          :show-poster="false"
          :feed="feed"
          :show-play-btn="showPlayBtn"
          :options="videoOptions"
          :play-icon-click="playIconClick"
          :mini-app-path-click-play="miniAppPathClickPlay"
          @on-play="onPlay"
          @on-playing="onPlaying"
          @on-pause="onPause"
          @on-timeupdate="onTimeupdate"
          @on-ended="onEnded"
          @on-prevent-autoplay="onPreventAutoplay"
          @on-click-playStatus="onClickPlayStatus"
        />
      </div>
      <div
        v-if="actionWrapperExist"
        class="tools-wrapper"
      >
        <transition name="fade">
          <div
            v-show="!showCollection"
            class="animate"
          >
            <player-info
              :feed="feed"
              :is-play="isPlay"
              :current-time="currentTime"
              @on-click-collection="onClickCollection"
            />
            <player-progress :progress="progress" />
          </div>
        </transition>
        <div
          v-if="(initOldUI || initPlayRec)"
          class="show-btn"
          @click="clickShowBtn"
        >
          <div>{{ showCollection ? '收起' : '更多视频' }}<i :class="{ show: showCollection }" /></div>
        </div>
      </div>
      <div
        v-if="isShowFeedDesc"
        class="feed-desc"
      >
        {{ feed.videoConfig?.title || feed.feedDesc }}
      </div>
    </div>
    <transition name="slide">
      <CollectionDesc
        v-show="showNewUI"
        :feed="feed"
      />
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed, ref, watch } from 'vue';
import { useStore, MutationTypes } from '@/store/index';
import { VideoJsPlayer } from 'video.js';
import { ModelMetaFeed } from '@/types/model';
import { EMiniAppPosition } from '@/constants/mini-app';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useVideoEventHandler from '@/composable/use-video-event-handler';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import { userSwiperItemReport } from '@/composable/use-compoent-report-methods';
import PlayerInfo from './player-info.vue';
import CollectionDesc from './collection-desc.vue';
import VideoPlayer from './common/video-player.vue';
import PlayerProgress from './common/player-progress.vue';
import { EVideoEvtNameTypes } from '@/constants/video';
import { GROWTH_POPUP } from '@/constants/ab-test';

const emits = defineEmits(['on-ended']);
// props
interface Props {
  feed: ModelMetaFeed,
  isLastFeed: boolean,
  initPlayRec: boolean,
  playIconClick: Function,
  showRecInPlayPage: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  isLastFeed: false,
  initPlayRec: false,
});
const {
  feed,
  isLastFeed,
  initPlayRec,
  playIconClick,
  showRecInPlayPage,
} = toRefs(props);

// state
const store = useStore();
const { state } = store;
const activeIndex = computed(() => state.activeIndex);
const isCollection = computed(() => state.isCollection);
const showCollection = computed(() => state.showCollection);
const isNewUI = computed(() => state.isNewUI);
const autoPlay = computed(() => {
  const { isNewUI, growthTestData, isGrowthPopup } = state;
  const hitGrowthTestRule = isNewUI
      && growthTestData
      && !growthTestData.collectionPullNew
      && !growthTestData.collectionPullOld;
  const hitGrowthPopup = isGrowthPopup === GROWTH_POPUP.NO;
  if ((hitGrowthPopup || hitGrowthTestRule) && feed.value.index === activeIndex.value) {
    // 增长实验拉新拉活开关为开才自动播放
    return true;
  }
  return false;
});
const videoOptions = computed(() => ({
  autoPlay: autoPlay.value,
  preload: 'meta',
}));

// data
const playerRef = ref<null | { player: VideoJsPlayer}>(null);
const isPlay = ref(false);
const currentTime = ref(0);
const progress = ref(0);
const showPlayBtn = ref(!autoPlay.value); // 是否展示播放按钮

// composable
const { getMiniAppPath } = useGetCommonMethods();
const swiperItemReport = userSwiperItemReport({ feed, playerRef, currentTime });
const { info: urlInfo } = useProvideUrlInfo();
// 是否初始化转评赞等互动区域
const actionWrapperExist = computed(() => {
  const { vtype } = urlInfo.query;
  if (vtype === 'interactive') {
    return false;
  }
  return true;
});

const isShowFeedDesc = computed(() => {
  const { showInfo } = urlInfo.query;
  if (showInfo) {
    return true;
  }
  return false;
});

const { useVideoEvent } = useVideoEventHandler();
useVideoEvent((params) => {
  const player = playerRef.value?.player;
  if (!player) return;
  const isActive = params.index === feed.value.index; // 是否是当前视频

  switch (params.eventName) {
    case EVideoEvtNameTypes.readyPlay:
      // 收到视频"准备播放"请求，其他视频需要stop
      if (!isActive) {
        player.currentTime(0);
        player.pause();
      }
      break;
    // 收到视频"播放暂停切换"请求
    case EVideoEvtNameTypes.changePlayOrPause:
      if (isActive) {
        player.paused() ? player.play() : player.pause();
      }
      break;
    // 收到视频"播放"请求，其他视频需要stop
    case EVideoEvtNameTypes.play:
      if (isActive) {
        player.play();
      } else {
        player.currentTime(0);
        player.pause();
      }
      break;
  };
});

// computed
const isActive = computed(() => feed.value.index === activeIndex.value);
const showNewUI = computed(() => showCollection.value && isNewUI.value);
const initOldUI = computed(() => !isNewUI.value && isCollection.value);
const isCreatePlayer = computed(() => {
  if (!isNewUI.value) {
    return true;
  }
  return Math.abs(feed.value.index - activeIndex.value) <= 2;
});
const miniAppPathClickPlay = computed(() => getMiniAppPath(EMiniAppPosition.video));

const onPlay = () => {
  isPlay.value = true;
  swiperItemReport.onPlay();
};
const onPlaying = () => {
  // 展示精选集时不显示播放按钮
  // 播放后点击可展示播放按钮
  // if (!showCollection.value) showPlayBtn.value = true;
  if (isActive.value) {
    store.commit(MutationTypes.SET_ACTIVE_PLAYER_PLAYING, { value: true });
  };
};
const onPause = () => {
  isPlay.value = false;
  if (isActive.value) {
    store.commit(MutationTypes.SET_ACTIVE_PLAYER_PLAYING, { value: false });
  };
  swiperItemReport.onPause();
};
const onTimeupdate = (timeProgress: number) => {
  if (!playerRef.value) return;
  progress.value = timeProgress;
  currentTime.value = playerRef.value.player.currentTime();
};
const onPreventAutoplay = () => {
  // 展示精选集时不显示播放按钮
  // 自动播放失败时展示给给用户播放按钮
  if (!showCollection.value) showPlayBtn.value = true;
};
const onClickPlayStatus = () => {
  if (showCollection.value) {
    store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: false });
    // showPlayBtn.value = true;
  }
};
const onEnded = () => {
  emits('on-ended');
  if (!playerRef.value) return;
  swiperItemReport.onEnded({
    isLastFeed: isLastFeed.value,
  });
};
const onClickCollection = () => {
  store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: true });
  // if (isNewUI.value) showPlayBtn.value = false;
};
const clickShowBtn = () => {
  store.commit(MutationTypes.SET_SHOW_COLLECTION, { value: !showCollection.value });
  swiperItemReport.clickShowBtn(showRecInPlayPage.value);
};

watch(activeIndex, () => {
  currentTime.value = 0;
});

</script>

<style lang="scss" scoped>
.swiper-item,
.scale-item {
  width: 100%;
  height: 100%;
}
.swiper-item {
  position: relative;
}
.scale-item {
  position: relative;
  transition: 0.3s ease;
  transform-origin: left 50%;
  transform: translateZ(0);
  &.show-collection {
    @include responsiveTransform(2.6667, 0, 0, 'vw');
    transform: scale(0.725);
  }
  .video-poster {
    height: 100%;
    width: 100%;
    position: absolute;
    margin: 0;
    padding: 0;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    background-size: contain;
  }
  .tools-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    bottom: constant(safe-area-inset-bottom);
    bottom: env(safe-area-inset-bottom);
    pointer-events: none;
    overflow: hidden;
    .animate {
      transform: translateZ(0);
    }
    .show-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      @include responsiveStyleSingle(height, 14.9333, 'vw');
      div {
        display: flex;
        align-items: center;
        justify-content: center;
        @include responsiveStyleSingle(width, 24, 'vw');
        @include responsiveStyleSingle(height, 5.8667, 'vw');
        @include responsiveStyleSingle(font-size, 3.2, 'vw');
        @include responsiveStyleSingle(border-radius, 3.2, 'vw');
        background: rgba(59, 59, 59, 0.8);
      }
      i {
        display: block;
        @include responsiveStyleSingle(width, 5.3333, 'vw');
        @include responsiveStyleSingle(height, 5.3333, 'vw');
        background: url('https://qzonestyle.gtimg.cn/aoi/sola/20200302114737_YPEVFPjpOL.png');
        background-size: 100% 100%;
        &.show {
          background: url('https://qzonestyle.gtimg.cn/aoi/sola/20200227200838_kaBS0Wm8a5.png');
          background-size: 100% 100%;
        }
      }
    }
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.3s;
    transition-timing-function: cubic-bezier(0, 0, 0.19, 0.97) !important;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  :deep(.mask) {
    transform: translateZ(0);
  }
  .feed-desc {
    position: absolute;
    @include responsiveStyleSingle(bottom, 80, 'px');
    @include responsiveStyleSingle(padding-left, 4, 'vw');
    @include responsiveStyleSingle(padding-right, 4, 'vw');
  }
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s;
}
.slide-enter,
.slide-leave-to {
  opacity: 0;
  transform: translate3d(0, 100%, 0);
}
</style>
